import React from 'react';
import '@inmoment/the-kitchen'; // Make sure DebugService actually loads
import { TitleText } from '../../../cookbook-ui/custom-components/TitleText/TitleText';
import { Page } from '../../../cookbook-ui/Page_SC';
import styled from 'styled-components';
import { DefaultButton, Dropdown, DropdownObject, InputField } from '@inmoment/the-kitchen';
import { useLocation, useNavigate } from 'react-router-dom';

const SpacedChildren = styled.div<{ gap?: number }>`
  display: flex;
  flex-direction: column;
  gap: ${({ gap = 48 }) => gap}px;
`;

// A custom hook that builds on useLocation to parse
// the query string for you.
function useQuery() {
  const { search } = useLocation();

  return React.useMemo(() => new URLSearchParams(search), [search]);
}

export function useCurrentUserInfo() {
  return {
    email: localStorage.getItem('user_profile_email'),
    team: localStorage.getItem('user_profile_team'),
  };
}

export function useUserInfo() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const fullStory = (window as any).FS;
  const hasIdentifiedFullStory = React.useRef(false);
  const hasIdentifiedStonly = React.useRef(false);

  React.useEffect(() => {
    const dataExpiration = 7776000000; //90 days
    const setTime = Number(localStorage.getItem('user_profile_set_date'));
    const email = localStorage.getItem('user_profile_email');
    const team = localStorage.getItem('user_profile_team');

    if (!team || !email || !setTime || Date.now() > setTime + dataExpiration) {
      pathname !== '/UserInfo' && navigate('/UserInfo');
    } else {
      if (fullStory && !hasIdentifiedFullStory.current) {
        hasIdentifiedFullStory.current = true;
        fullStory.identify(email, {
          displayName: email,
          email: email,
          // Add your own custom user variables here, details at
          // https://help.fullstory.com/hc/en-us/articles/360020623294-FS-setUserVars-API-Capturing-custom-user-properties
          team: team,
          environment: 'Cookbook',
        });
      }

      if (!hasIdentifiedStonly.current) {
        hasIdentifiedStonly.current = true;
        StonlyWidget('identify', email, { email, team });
      }
    }
  }, [fullStory, navigate, pathname]);
}

export const UserInfo: React.VFC = () => {
  const data = [
    { title: 'Admin', id: 'AD' },
    { title: 'Data Exploration', id: 'DX' },
    { title: 'Field Reporting', id: 'FR' },
    { title: 'Garage', id: 'UX' },
    { title: 'Kitchen', id: 'KX' },
    { title: 'Spotlight', id: 'SP' },
    { title: 'Survey', id: 'SU' },
    { title: 'Text Analytics', id: 'TA' },
    { title: 'Workflow', id: 'WO' },
    { title: 'Other', id: 'OT' },
  ];

  const findTeam = (team: string | null) => {
    if (!team) {
      return undefined;
    } else {
      const dataItem = data.find((item) => item.title === team);
      return dataItem || data.find((item) => item.title === 'Other');
    }
  };

  const storedTeam = findTeam(localStorage.getItem('user_profile_team'));

  const [userId, setUserId] = React.useState(localStorage.getItem('user_profile_email') || '');
  const [team, setTeam] = React.useState<DropdownObject | undefined>(storedTeam);
  const [other, setOther] = React.useState(storedTeam?.title === 'Other' ? localStorage.getItem('user_profile_team') || '' : '');
  const query = useQuery();
  const clear = query.get('clear');
  const navigate = useNavigate();

  React.useEffect(() => {
    if (clear) {
      console.log(`clearing`);
      onErase();
      navigate('/UserInfo');
    }
  }, [clear, navigate]);

  const isSaveDisabled = !team?.title || (team?.title === 'Other' && !other) || !userId.toLowerCase().match(/.*@inmoment\.com/gm);

  const onSave = () => {
    localStorage.setItem('user_profile_email', userId);
    localStorage.setItem('user_profile_team', team?.title === 'Other' ? other : team?.title || 'Other');
    localStorage.setItem('user_profile_set_date', Date.now().toString());
    navigate('/');
  };

  const onErase = () => {
    localStorage.removeItem('user_profile_email');
    localStorage.removeItem('user_profile_team');
    localStorage.removeItem('user_profile_set_date');
  };

  return (
    <>
      <Page>
        <SpacedChildren>
          <div>
            <TitleText
              headerText="Update Your Information"
              isLargeHeader
              footerText={
                <>
                  In order to provide you with the most relevant information and to allow the Kitchen team to focus their efforts on the most used
                  parts of the documentation we are requesting you update your user profile to include your InMoment username and team name.
                  <br />
                  <br />
                  <b>Providing this information is mandatory in order to use the Cookbook.</b>
                </>
              }
            />
          </div>

          <form>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18, maxWidth: 600 }}>
              <div style={{ display: 'flex', gap: 12 }}>
                <InputField onChange={(e) => setUserId(e.target.value)} value={userId} label="InMoment Email" name="email" type="email"></InputField>
                <Dropdown
                  label="Team Name"
                  data={data}
                  value={team?.id}
                  name="team"
                  onValueChange={(value) => setTeam((value as { selected: DropdownObject }).selected)}
                ></Dropdown>
              </div>
              {team?.title === 'Other' && (
                <InputField onChange={(e) => setOther(e.target.value)} value={other} name="otherTeam" label={'Team Name'}></InputField>
              )}
              <DefaultButton
                type="submit"
                text="Save"
                disabled={isSaveDisabled}
                tooltipDisplayOnDisabled={true}
                tooltip={{
                  title: 'Please ensure that your team is selected and emailed entered in the form of your_username@inmmoment.com',
                  direction: 7,
                  enabled: isSaveDisabled,
                }}
                onKeyDown={onSave}
                onClick={onSave}
              ></DefaultButton>
            </div>
          </form>
        </SpacedChildren>
      </Page>
    </>
  );
};
